import {
  Box,
  Checkbox,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import type { EnrichedDeferredLine } from "../../services/distributionPreviewService";

/**
 * Tabla anidada de productos individuales que se muestra al expandir un
 * bucket de proveedor en el panel "Líneas diferidas". Extraído de
 * `DeferredBySupplierPanel.tsx` por responsabilidad y para respetar P7
 * (≤500 L).
 *
 * Columnas: checkbox | Producto | Origen | Stock origen | Qty diferida | ABC.
 *
 * Reglas:
 *   - Checkbox individual deshabilitado cuando `stock_en_origen <= 0`.
 *   - Filas sin stock con opacity reducida (no se pueden seleccionar).
 *   - Stock en verde si > 0, en rojo si 0 (señal visual).
 *   - ABC con chip coloreado: A=verde, B=azul, C/null=gris/dash.
 */
export interface DeferredSupplierLinesTableProps {
  lines: EnrichedDeferredLine[];
  selected: Set<string>;
  onToggleLine: (line: EnrichedDeferredLine) => void;
}

export function DeferredSupplierLinesTable({
  lines,
  selected,
  onToggleLine,
}: DeferredSupplierLinesTableProps) {
  return (
    <TableContainer
      sx={{
        bgcolor: "grey.50",
        borderTop: 1,
        borderColor: "divider",
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "grey.100" }}>
            <TableCell padding="checkbox" sx={{ width: 40 }} />
            <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
              Producto
            </TableCell>
            <TableCell
              sx={{ fontWeight: 600, fontSize: "0.75rem", width: 80 }}
            >
              Origen
            </TableCell>
            <TableCell
              align="right"
              sx={{ fontWeight: 600, fontSize: "0.75rem", width: 110 }}
            >
              Stock origen
            </TableCell>
            <TableCell
              align="right"
              sx={{ fontWeight: 600, fontSize: "0.75rem", width: 110 }}
            >
              Qty diferida
            </TableCell>
            <TableCell
              align="center"
              sx={{ fontWeight: 600, fontSize: "0.75rem", width: 60 }}
            >
              ABC
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.map((line) => {
            const noStock = line.stock_en_origen <= 0;
            const isSelected = selected.has(line.recommendation_id);
            return (
              <TableRow
                key={line.recommendation_id}
                hover={!noStock}
                selected={isSelected}
                sx={{
                  opacity: noStock ? 0.5 : 1,
                  "& > td": { fontSize: "0.75rem" },
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={isSelected}
                    disabled={noStock}
                    onChange={() => onToggleLine(line)}
                    inputProps={{
                      "aria-label": `Seleccionar ${
                        line.product_name ?? line.unified_product_id
                      }`,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Tooltip
                    title={line.product_name ?? line.unified_product_id}
                    placement="top"
                  >
                    <Box
                      sx={{
                        maxWidth: 480,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {line.product_name ?? line.unified_product_id}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ fontFamily: "monospace", color: "text.secondary" }}
                  >
                    {line.origin_warehouse}
                  </Typography>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontVariantNumeric: "tabular-nums" }}
                >
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: noStock ? "error.main" : "success.main",
                    }}
                  >
                    {line.stock_en_origen.toLocaleString("es-MX")}
                  </Typography>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontVariantNumeric: "tabular-nums",
                    fontWeight: 500,
                  }}
                >
                  {line.qty_planned.toLocaleString("es-MX")}
                </TableCell>
                <TableCell align="center">
                  {line.abc ? (
                    <Chip
                      label={line.abc}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        bgcolor:
                          line.abc === "A"
                            ? "success.light"
                            : line.abc === "B"
                              ? "info.light"
                              : "grey.200",
                        color:
                          line.abc === "A"
                            ? "success.dark"
                            : line.abc === "B"
                              ? "info.dark"
                              : "text.secondary",
                      }}
                    />
                  ) : (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.disabled"
                    >
                      —
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
